import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ContactSection from "@/components/ContactSection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Brain,
  Bot,
  Code2,
  Smartphone,
  BookOpen,
  CheckCircle2,
  XCircle,
  Star,
  Zap,
  Shield,
  Globe,
  Search,
  Cpu,
  MessageSquare,
  Award,
  Layers,
  FileText,
  Headphones,
  Users,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

const InteligenciaArtificial = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    name: "Guía de Inteligencia Artificial: Comparativas y Herramientas",
    provider: {
      "@type": "Organization",
      name: "erm-media",
    },
    description:
      "Comparativa completa entre los principales asistentes de inteligencia artificial: Claude, ChatGPT, Copilot, Gemini y DeepSeek. Qué es NotebookLM y la mejor IA para desarrollo móvil.",
    keywords:
      "inteligencia artificial, Claude, ChatGPT, Copilot, Gemini, DeepSeek, NotebookLM, desarrollo móvil, iOS, Android",
  };

  const aiComparativa = [
    {
      name: "Claude",
      company: "Anthropic",
      color: "from-orange-500/20 to-orange-500/5",
      badge: "bg-orange-500/10 text-orange-600",
      icon: <Brain className="w-6 h-6 text-orange-500" />,
      bestFor: "Análisis, redacción y código complejo",
      context: "200K tokens",
      imageGen: false,
      freeTier: true,
      codeQuality: 5,
      strengths: ["Contexto enorme (200K tokens)", "Muy seguro y honesto", "Excelente para análisis profundo", "Código de alta calidad"],
      weaknesses: ["Sin generación de imágenes", "Menos integraciones de terceros"],
    },
    {
      name: "ChatGPT",
      company: "OpenAI",
      color: "from-green-500/20 to-green-500/5",
      badge: "bg-green-500/10 text-green-600",
      icon: <MessageSquare className="w-6 h-6 text-green-500" />,
      bestFor: "Uso general, creatividad y plugins",
      context: "128K tokens",
      imageGen: true,
      freeTier: true,
      codeQuality: 5,
      strengths: ["DALL-E para imágenes", "Ecosistema amplio (GPTs)", "Voz y visión", "Code Interpreter"],
      weaknesses: ["Más caro en API", "Puede alucinar con más frecuencia"],
    },
    {
      name: "Copilot",
      company: "Microsoft",
      color: "from-blue-500/20 to-blue-500/5",
      badge: "bg-blue-500/10 text-blue-600",
      icon: <Code2 className="w-6 h-6 text-blue-500" />,
      bestFor: "Integración con Office 365 y código",
      context: "128K tokens",
      imageGen: true,
      freeTier: true,
      codeQuality: 5,
      strengths: ["Integrado en Visual Studio", "Microsoft 365 nativo", "Búsqueda web en tiempo real", "DALL-E 3 integrado"],
      weaknesses: ["Requiere suscripción Microsoft para funciones avanzadas", "Menos flexible en API"],
    },
    {
      name: "Gemini",
      company: "Google",
      color: "from-purple-500/20 to-purple-500/5",
      badge: "bg-purple-500/10 text-purple-600",
      icon: <Search className="w-6 h-6 text-purple-500" />,
      bestFor: "Android Studio y Google Workspace",
      context: "2M tokens",
      imageGen: true,
      freeTier: true,
      codeQuality: 4,
      strengths: ["Contexto de 2M tokens (mayor)", "Nativo en Android Studio", "Google Workspace integrado", "Búsqueda web en tiempo real"],
      weaknesses: ["Respuestas menos precisas en tareas complejas", "Integración API más compleja"],
    },
    {
      name: "DeepSeek",
      company: "DeepSeek AI",
      color: "from-cyan-500/20 to-cyan-500/5",
      badge: "bg-cyan-500/10 text-cyan-600",
      icon: <Zap className="w-6 h-6 text-cyan-500" />,
      bestFor: "Código y relación coste-rendimiento",
      context: "128K tokens",
      imageGen: false,
      freeTier: true,
      codeQuality: 5,
      strengths: ["Código de excelente calidad", "Muy económico (API)", "Open source disponible", "Razonamiento matemático"],
      weaknesses: ["Empresa China (privacidad)", "Sin generación de imágenes", "Menos integraciones"],
    },
  ];

  const notebookLMFeatures = [
    {
      icon: <FileText className="w-5 h-5 text-primary" />,
      title: "Analiza tus documentos",
      description: "Sube PDFs, Google Docs, presentaciones o enlaces web y el modelo los analiza completamente.",
    },
    {
      icon: <MessageSquare className="w-5 h-5 text-primary" />,
      title: "Preguntas y respuestas",
      description: "Haz preguntas sobre tu material y obtén respuestas precisas con citas de las fuentes.",
    },
    {
      icon: <BookOpen className="w-5 h-5 text-primary" />,
      title: "Guías de estudio",
      description: "Genera resúmenes, preguntas de repaso, líneas de tiempo y mapas conceptuales automáticamente.",
    },
    {
      icon: <Headphones className="w-5 h-5 text-primary" />,
      title: "Audio Overview (Podcast)",
      description: "Convierte tus documentos en conversaciones de podcast entre dos voces de IA, ideal para aprender.",
    },
    {
      icon: <Search className="w-5 h-5 text-primary" />,
      title: "Citas precisas",
      description: "Cada respuesta incluye referencias exactas al documento de origen, evitando alucinaciones.",
    },
    {
      icon: <Shield className="w-5 h-5 text-primary" />,
      title: "Privacidad de datos",
      description: "Tus documentos no se usan para entrenar el modelo de Google. Seguro para información sensible.",
    },
  ];

  const notebookLMUseCases = [
    {
      icon: <Users className="w-5 h-5 text-orange-500" />,
      title: "Estudiantes",
      cases: ["Resumir libros y apuntes", "Crear guías de examen", "Entender conceptos complejos", "Generar preguntas de práctica"],
    },
    {
      icon: <Search className="w-5 h-5 text-orange-500" />,
      title: "Investigadores",
      cases: ["Analizar múltiples papers", "Encontrar conexiones entre fuentes", "Síntesis bibliográfica", "Gestionar notas de investigación"],
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-orange-500" />,
      title: "Profesionales",
      cases: ["Analizar informes empresariales", "Resúmenes de reuniones", "Documentación técnica", "Contratos y documentos legales"],
    },
  ];

  const mobileAI = [
    {
      name: "GitHub Copilot",
      company: "Microsoft / GitHub",
      rating: 5,
      icon: <Code2 className="w-6 h-6 text-blue-500" />,
      color: "border-blue-500/30 bg-blue-500/5",
      badgeColor: "bg-blue-500/10 text-blue-600",
      badge: "Mejor integración IDE",
      ios: true,
      android: true,
      features: [
        "Plugin nativo para Xcode (iOS)",
        "Plugin nativo para Android Studio",
        "Autocompletado de código en tiempo real",
        "Generación de funciones completas",
        "Tests unitarios automáticos",
      ],
      verdict: "La mejor opción para productividad diaria en IDE.",
    },
    {
      name: "Claude",
      company: "Anthropic",
      rating: 5,
      icon: <Brain className="w-6 h-6 text-orange-500" />,
      color: "border-orange-500/30 bg-orange-500/5",
      badgeColor: "bg-orange-500/10 text-orange-600",
      badge: "Mejor para arquitectura",
      ios: true,
      android: true,
      features: [
        "Excelente para diseño de arquitectura",
        "Revisión y refactorización de código",
        "Explicaciones detalladas de Swift/Kotlin",
        "Análisis de bugs complejos",
        "Contexto de 200K tokens (proyectos grandes)",
      ],
      verdict: "Ideal para problemas complejos y revisiones de código.",
    },
    {
      name: "Gemini",
      company: "Google",
      rating: 4,
      icon: <Search className="w-6 h-6 text-purple-500" />,
      color: "border-purple-500/30 bg-purple-500/5",
      badgeColor: "bg-purple-500/10 text-purple-600",
      badge: "Mejor para Android",
      ios: false,
      android: true,
      features: [
        "Integración nativa en Android Studio (Gemini in Android Studio)",
        "Autocompletado para Kotlin/Java",
        "Conocimiento actualizado de APIs Android",
        "Generación de layouts XML",
        "Integración con Firebase",
      ],
      verdict: "La elección natural para desarrolladores Android.",
    },
    {
      name: "ChatGPT",
      company: "OpenAI",
      rating: 4,
      icon: <MessageSquare className="w-6 h-6 text-green-500" />,
      color: "border-green-500/30 bg-green-500/5",
      badgeColor: "bg-green-500/10 text-green-600",
      badge: "Más versátil",
      ios: true,
      android: true,
      features: [
        "Gran comunidad y ejemplos disponibles",
        "GPTs especializados en Swift y Kotlin",
        "Bueno para preguntas generales",
        "Code Interpreter para análisis",
        "Plugin para VS Code",
      ],
      verdict: "Buena opción general con mucho contenido de la comunidad.",
    },
    {
      name: "DeepSeek",
      company: "DeepSeek AI",
      rating: 4,
      icon: <Zap className="w-6 h-6 text-cyan-500" />,
      color: "border-cyan-500/30 bg-cyan-500/5",
      badgeColor: "bg-cyan-500/10 text-cyan-600",
      badge: "Mejor relación coste",
      ios: true,
      android: true,
      features: [
        "Excelente calidad de código",
        "API muy económica",
        "Razonamiento lógico sólido",
        "Buen conocimiento de Swift/Kotlin",
        "Disponible vía API para integraciones",
      ],
      verdict: "Excelente si buscas calidad al menor coste de API.",
    },
  ];

  const renderStars = (count: number) =>
    Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i < count ? "text-orange-500 fill-orange-500" : "text-muted-foreground/30"}`}
      />
    ));

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Inteligencia Artificial: Guía Comparativa | erm-media"
        description="Comparativa completa entre Claude, ChatGPT, Copilot, Gemini y DeepSeek. Descubre qué es NotebookLM y cuál es la mejor IA para desarrollo iOS y Android."
        keywords="inteligencia artificial, Claude vs ChatGPT, Copilot, Gemini, DeepSeek, NotebookLM, desarrollo móvil iOS Android, comparativa IA 2024"
        canonicalUrl="https://erm-media.com/inteligencia-artificial"
        structuredData={structuredData}
      />
      <Header />

      {/* ──────────────────── HERO ──────────────────── */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Brain className="w-4 h-4" />
              <span className="text-sm font-medium">Guía de Inteligencia Artificial</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Todo lo que necesitas saber sobre
              <span className="text-orange-500 block">Inteligencia Artificial</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Comparativas, herramientas y recomendaciones prácticas para elegir el asistente de IA
              que mejor se adapta a tus necesidades profesionales y de desarrollo.
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              {["Claude vs ChatGPT", "Comparativa de IAs", "NotebookLM", "IA para Mobile Dev"].map((item) => (
                <span
                  key={item}
                  className="bg-muted text-muted-foreground px-4 py-1.5 rounded-full text-sm border border-border/50"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────── SECCIÓN 1: Claude vs ChatGPT ──────────────────── */}
      <section id="claude-vs-chatgpt" className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Layers className="w-4 h-4" />
              <span className="text-sm font-medium">Sección 1</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Claude <span className="text-orange-500">vs</span> ChatGPT
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Los dos asistentes de IA más poderosos del mercado. Ambos excelentes, pero con
              enfoques y fortalezas distintas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Claude card */}
            <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-orange-500/5 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center">
                    <Brain className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Claude</CardTitle>
                    <CardDescription>by Anthropic</CardDescription>
                  </div>
                  <Badge className="ml-auto bg-orange-500/10 text-orange-600 border-orange-500/20 hover:bg-orange-500/20">
                    Constitucional AI
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Contexto máximo</p>
                    <p className="font-semibold text-foreground">200K tokens</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Modelos</p>
                    <p className="font-semibold text-foreground">Haiku · Sonnet · Opus</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Precio Pro</p>
                    <p className="font-semibold text-foreground">$20/mes</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Generación imágenes</p>
                    <p className="font-semibold text-foreground">No incluido</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Fortalezas</p>
                  <ul className="space-y-1.5">
                    {[
                      "Contexto enorme: lee documentos completos",
                      "Muy honesto — admite sus limitaciones",
                      "Excelente análisis y razonamiento",
                      "Código limpio y bien documentado",
                      "Diseñado con seguridad como prioridad",
                    ].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Limitaciones</p>
                  <ul className="space-y-1.5">
                    {["Sin generación de imágenes nativa", "Menos plugins / integraciones de terceros"].map((w) => (
                      <li key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-orange-500/10 rounded-lg p-3 text-sm text-orange-700 dark:text-orange-400">
                  <strong>Ideal para:</strong> análisis de documentos largos, escritura profesional, código con alta exigencia de calidad.
                </div>
              </CardContent>
            </Card>

            {/* ChatGPT card */}
            <Card className="border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-500/5 hover:shadow-lg transition-all">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">ChatGPT</CardTitle>
                    <CardDescription>by OpenAI</CardDescription>
                  </div>
                  <Badge className="ml-auto bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20">
                    GPT-4o / o1
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Contexto máximo</p>
                    <p className="font-semibold text-foreground">128K tokens</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Modelos</p>
                    <p className="font-semibold text-foreground">GPT-4o · o1 · o3</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Precio Plus</p>
                    <p className="font-semibold text-foreground">$20/mes</p>
                  </div>
                  <div className="bg-background/60 rounded-lg p-3">
                    <p className="text-muted-foreground text-xs mb-1">Generación imágenes</p>
                    <p className="font-semibold text-foreground">DALL-E 3</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Fortalezas</p>
                  <ul className="space-y-1.5">
                    {[
                      "DALL-E 3 para generar imágenes",
                      "Amplio ecosistema de GPTs personalizados",
                      "Entrada de voz y análisis de imágenes",
                      "Code Interpreter: ejecuta código en sandbox",
                      "Gran comunidad y abundantes recursos",
                    ].map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Limitaciones</p>
                  <ul className="space-y-1.5">
                    {["API más costosa que alternativas", "Contexto menor que Claude (128K vs 200K)"].map((w) => (
                      <li key={w} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-green-500/10 rounded-lg p-3 text-sm text-green-700 dark:text-green-400">
                  <strong>Ideal para:</strong> creatividad, generación de imágenes, proyectos multimodales y acceso a un ecosistema muy amplio.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resumen */}
          <div className="max-w-5xl mx-auto mt-8">
            <Card className="border-border/50 bg-muted/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground mb-1">Veredicto</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Ambos son excepcionales y rivales directos. <strong>Elige Claude</strong> si necesitas analizar
                      documentos largos, código de alta calidad o respuestas muy fiables. <strong>Elige ChatGPT</strong> si
                      necesitas generar imágenes, usar el ecosistema de plugins o trabajar con tareas multimodales.
                      Muchos profesionales usan ambos según la tarea.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ──────────────────── SECCIÓN 2: Comparativa múltiple ──────────────────── */}
      <section id="comparativa" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Bot className="w-4 h-4" />
              <span className="text-sm font-medium">Sección 2</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Comparativa de los <span className="text-orange-500">principales asistentes IA</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Claude, ChatGPT, Copilot, Gemini y DeepSeek analizados en detalle para que elijas el
              que mejor encaja con tu flujo de trabajo.
            </p>
          </div>

          {/* Cards grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {aiComparativa.map((ai) => (
              <Card
                key={ai.name}
                className={`border-border/50 hover:shadow-md transition-all bg-gradient-to-br ${ai.color}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-background/60 rounded-xl flex items-center justify-center">
                      {ai.icon}
                    </div>
                    <Badge className={`${ai.badge} border-0 text-xs`}>{ai.company}</Badge>
                  </div>
                  <CardTitle className="text-lg">{ai.name}</CardTitle>
                  <CardDescription className="text-xs">{ai.bestFor}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-background/60 rounded-lg p-2">
                      <p className="text-muted-foreground mb-0.5">Contexto</p>
                      <p className="font-semibold">{ai.context}</p>
                    </div>
                    <div className="bg-background/60 rounded-lg p-2">
                      <p className="text-muted-foreground mb-0.5">Imágenes</p>
                      <p className={`font-semibold ${ai.imageGen ? "text-green-600" : "text-muted-foreground"}`}>
                        {ai.imageGen ? "Sí" : "No"}
                      </p>
                    </div>
                    <div className="bg-background/60 rounded-lg p-2">
                      <p className="text-muted-foreground mb-0.5">Tier gratuito</p>
                      <p className="font-semibold text-green-600">Disponible</p>
                    </div>
                    <div className="bg-background/60 rounded-lg p-2">
                      <p className="text-muted-foreground mb-0.5">Código</p>
                      <div className="flex gap-0.5 mt-0.5">{renderStars(ai.codeQuality)}</div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1.5">Puntos fuertes</p>
                    <ul className="space-y-1">
                      {ai.strengths.map((s) => (
                        <li key={s} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1.5">A considerar</p>
                    <ul className="space-y-1">
                      {ai.weaknesses.map((w) => (
                        <li key={w} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                          <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabla comparativa rápida */}
          <div className="max-w-5xl mx-auto overflow-x-auto rounded-xl border border-border/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border/50">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">IA</th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground">Contexto</th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground">Imágenes</th>
                  <th className="text-center px-4 py-3 font-semibold text-foreground">Gratis</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground hidden md:table-cell">Mejor para</th>
                </tr>
              </thead>
              <tbody>
                {aiComparativa.map((ai, i) => (
                  <tr
                    key={ai.name}
                    className={`border-b border-border/30 ${i % 2 === 0 ? "bg-background" : "bg-muted/20"} hover:bg-muted/40 transition-colors`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {ai.icon}
                        <div>
                          <p className="font-semibold text-foreground">{ai.name}</p>
                          <p className="text-xs text-muted-foreground">{ai.company}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium text-foreground">{ai.context}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {ai.imageGen ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <XCircle className="w-4 h-4 text-muted-foreground/40 mx-auto" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs">{ai.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ──────────────────── SECCIÓN 3: NotebookLM ──────────────────── */}
      <section id="notebooklm" className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium">Sección 3</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              ¿Qué es <span className="text-orange-500">NotebookLM</span> y para qué sirve?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              La herramienta de Google que transforma tus documentos en un asistente inteligente
              personalizado, con citas precisas y sin alucinaciones.
            </p>
          </div>

          {/* Intro card */}
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="border-border/50 bg-gradient-to-br from-blue-500/5 to-purple-500/5">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center shrink-0">
                    <BookOpen className="w-8 h-8 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-3">¿Qué es NotebookLM?</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      <strong className="text-foreground">NotebookLM</strong> es una herramienta de inteligencia artificial
                      desarrollada por <strong className="text-foreground">Google</strong> que actúa como un asistente de
                      investigación personalizado. A diferencia de ChatGPT o Claude, NotebookLM no usa su conocimiento general
                      — trabaja <em>exclusivamente</em> con los documentos que tú le proporcionas.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Puedes subir PDFs, Google Docs, presentaciones, videos de YouTube o páginas web, y el modelo responderá
                      preguntas, generará resúmenes y citará exactamente de dónde proviene cada pieza de información.
                      Disponible gratis en <strong className="text-foreground">notebooklm.google.com</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Features grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {notebookLMFeatures.map((f) => (
              <Card key={f.title} className="border-border/50 hover:border-primary/30 hover:shadow-md transition-all group">
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                    {f.icon}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{f.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Use cases */}
          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl font-bold text-foreground text-center mb-8">
              ¿Quién debería usar NotebookLM?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {notebookLMUseCases.map((uc) => (
                <Card key={uc.title} className="border-border/50 hover:shadow-md transition-all">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                        {uc.icon}
                      </div>
                      <CardTitle className="text-base">{uc.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {uc.cases.map((c) => (
                        <li key={c} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ArrowRight className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-8 border-orange-500/20 bg-orange-500/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground mb-1">La función estrella: Audio Overview</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      NotebookLM puede convertir tus documentos en un podcast generado por IA con dos presentadores virtuales
                      que debaten y explican el contenido. Ideal para aprender mientras conduces, haces ejercicio o realizas
                      tareas. Es una de las funciones más innovadoras de cualquier herramienta de IA actualmente disponible.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ──────────────────── SECCIÓN 4: Mejor IA para Mobile Dev ──────────────────── */}
      <section id="ia-mobile-dev" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
              <Smartphone className="w-4 h-4" />
              <span className="text-sm font-medium">Sección 4</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Mejor IA para desarrollo{" "}
              <span className="text-orange-500">iOS y Android</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comparativa especializada en desarrollo móvil: desde la integración en el IDE hasta la
              calidad del código Swift y Kotlin generado.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-10">
            {mobileAI.map((ai) => (
              <Card
                key={ai.name}
                className={`border ${ai.color} hover:shadow-lg transition-all`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center shadow-sm">
                      {ai.icon}
                    </div>
                    <Badge className={`${ai.badgeColor} border-0 text-xs`}>{ai.badge}</Badge>
                  </div>
                  <CardTitle className="text-base">{ai.name}</CardTitle>
                  <CardDescription className="text-xs">{ai.company}</CardDescription>

                  <div className="flex items-center gap-3 pt-1">
                    <div className="flex gap-0.5">{renderStars(ai.rating)}</div>
                    <div className="flex gap-2 ml-auto">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${ai.ios ? "bg-blue-500/10 text-blue-600" : "bg-muted text-muted-foreground/50 line-through"}`}>
                        iOS
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${ai.android ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground/50 line-through"}`}>
                        Android
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-1.5">
                    {ai.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="bg-background/60 rounded-lg p-3 text-xs text-muted-foreground border border-border/30">
                    <strong className="text-foreground">Veredicto: </strong>{ai.verdict}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recomendación final */}
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
            <Card className="border-blue-500/30 bg-blue-500/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-foreground">Para iOS (Swift / Xcode)</h3>
                </div>
                <ol className="space-y-2">
                  {[
                    { rank: "1°", name: "GitHub Copilot", reason: "Integración nativa en Xcode" },
                    { rank: "2°", name: "Claude", reason: "Mejor para arquitectura y código complejo" },
                    { rank: "3°", name: "ChatGPT", reason: "Comunidad y recursos abundantes" },
                  ].map((r) => (
                    <li key={r.name} className="flex items-center gap-3 text-sm">
                      <span className="w-7 h-7 bg-blue-500/20 text-blue-600 font-bold text-xs rounded-full flex items-center justify-center shrink-0">
                        {r.rank}
                      </span>
                      <span>
                        <strong className="text-foreground">{r.name}</strong>
                        <span className="text-muted-foreground"> — {r.reason}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            <Card className="border-green-500/30 bg-green-500/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="font-bold text-foreground">Para Android (Kotlin / Android Studio)</h3>
                </div>
                <ol className="space-y-2">
                  {[
                    { rank: "1°", name: "Gemini", reason: "Nativo en Android Studio + Firebase" },
                    { rank: "2°", name: "GitHub Copilot", reason: "Plugin disponible, alta productividad" },
                    { rank: "3°", name: "Claude", reason: "Excelente para arquitectura y revisiones" },
                  ].map((r) => (
                    <li key={r.name} className="flex items-center gap-3 text-sm">
                      <span className="w-7 h-7 bg-green-500/20 text-green-600 font-bold text-xs rounded-full flex items-center justify-center shrink-0">
                        {r.rank}
                      </span>
                      <span>
                        <strong className="text-foreground">{r.name}</strong>
                        <span className="text-muted-foreground"> — {r.reason}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            <Card className="md:col-span-2 border-orange-500/20 bg-orange-500/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-foreground mb-2">Recomendación final</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      La combinación ganadora para desarrollo móvil profesional es{" "}
                      <strong className="text-foreground">GitHub Copilot en el IDE</strong> para autocompletado y productividad
                      diaria, más <strong className="text-foreground">Claude</strong> en el navegador para diseño de
                      arquitectura, resolución de bugs complejos y revisiones de código. Para Android, sustituye o complementa
                      Copilot con <strong className="text-foreground">Gemini en Android Studio</strong> por su integración nativa
                      con el ecosistema Google.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ──────────────────── CTA / CONTACT ──────────────────── */}
      <ContactSection />

      <Footer />
    </div>
  );
};

export default InteligenciaArtificial;
